/**
 * Connection display state computation
 *
 * Implements the display state logic from CONNECTION_STATE_INTERFACE.md
 * Computes UI presentation from credentialStatus + state + deletedAt
 *
 * Priority order:
 * 1. deletedAt check (soft delete)
 * 2. Archived state
 * 3. Paused state
 * 4. Credential status (pending_auth, valid, refreshing, expired)
 */

import type {
  Connection,
  DisplayState,
} from '@/types/connection.types'

/**
 * Compute display state from connection fields
 *
 * COMPLETE STATE MATRIX - All permutations handled:
 *
 * Priority 1: deletedAt set              → "Removed" (gray) - shouldn't appear in list
 * Priority 2: state=archived             → "Archived" (gray)
 * Priority 3: pending_auth (any state)   → "Setup Incomplete" (gray)
 * Priority 4: paused + expired           → "Paused (Expired)" (orange-yellow)
 * Priority 5: active + expired           → "Needs Attention" (orange)
 * Priority 6: paused + valid/refreshing  → "Paused" (yellow)
 * Priority 7: refreshing                 → "Connected" (blue)
 * Priority 8: valid                      → "Connected" (green)
 *
 * Key insight: Credential issues (pending_auth, expired) are blockers that
 * prevent user actions from working. They should take priority over paused
 * state to avoid confusion when user tries to unpause/resume.
 *
 * @param connection - Connection entity with credentialStatus, state, deletedAt
 * @returns DisplayState with label, color, icon, description, and available actions
 */
export function computeDisplayState(connection: Connection): DisplayState {
  const { credentialStatus, state, deletedAt } = connection

  // Priority 1: Soft deleted - shouldn't appear in normal list views
  if (deletedAt !== null) {
    return {
      label: 'Removed',
      color: 'gray',
      icon: 'delete',
      description: 'Connection removed',
      actions: ['undelete'],
    }
  }

  // Priority 2: Archived - long-term inactive storage
  // Note: If credentials expired while archived, user will need to reconnect after restore
  if (state === 'archived') {
    const needsReauth = credentialStatus === 'expired' || credentialStatus === 'pending_auth'
    return {
      label: 'Archived',
      color: 'gray',
      icon: 'inbox',
      description: needsReauth
        ? 'Archived - will need reconnection after restore'
        : 'Connection archived',
      actions: ['restore', 'delete'],
    }
  }

  // Priority 3: OAuth flow not completed - blocks everything
  // Even if paused, auth must be completed first
  if (credentialStatus === 'pending_auth') {
    return {
      label: 'Setup Incomplete',
      color: 'gray',
      icon: 'disconnect',
      description: 'Connection started but not authorized',
      actions: ['reconnect', 'delete'],
    }
  }

  // Priority 4: Paused + expired - special combination
  // User sees paused state but needs to reconnect before resuming
  if (state === 'paused' && credentialStatus === 'expired') {
    return {
      label: 'Paused (Expired)',
      color: 'orange',
      icon: 'warning',
      description: 'Paused - reconnect required before resuming',
      actions: ['reconnect', 'delete'],
    }
  }

  // Priority 5: Active + expired - needs immediate attention
  if (credentialStatus === 'expired') {
    return {
      label: 'Needs Attention',
      color: 'orange',
      icon: 'warning',
      description: 'Authorization expired - reconnect to continue',
      actions: ['reconnect', 'pause', 'delete'],
    }
  }

  // Priority 6: Paused with valid/refreshing credentials
  if (state === 'paused') {
    return {
      label: 'Paused',
      color: 'yellow',
      icon: 'pause-circle',
      description: 'Syncing paused',
      actions: ['test', 'unpause', 'delete'],
    }
  }

  // Priority 7: Token refresh in progress (temporary state)
  if (credentialStatus === 'refreshing') {
    return {
      label: 'Connected',
      color: 'blue',
      icon: 'sync',
      description: 'Refreshing credentials',
      actions: ['test', 'pause', 'delete'],
    }
  }

  // Priority 8: Working normally (active + valid)
  return {
    label: 'Connected',
    color: 'green',
    icon: 'check-circle',
    description: 'Working normally',
    actions: ['test', 'pause', 'delete'],
  }
}

/**
 * Check if connection needs user attention (error states)
 *
 * @param connection - Connection entity
 * @returns true if connection requires user action (expired, pending auth)
 */
export function needsAttention(connection: Connection): boolean {
  const { credentialStatus, state, deletedAt } = connection

  // Deleted/archived connections don't need immediate attention
  if (deletedAt !== null || state === 'archived') {
    return false
  }

  // Active or paused connections with credential issues need attention
  return credentialStatus === 'expired' || credentialStatus === 'pending_auth'
}

/**
 * Check if connection is currently operational
 *
 * @param connection - Connection entity
 * @returns true if connection can sync data (active + valid credentials)
 */
export function isOperational(connection: Connection): boolean {
  const { credentialStatus, state, deletedAt } = connection

  return (
    deletedAt === null &&
    state === 'active' &&
    credentialStatus === 'valid'
  )
}

/**
 * Get user-friendly platform display name
 *
 * @param platform - Platform value from connection
 * @returns Display name with environment indicator
 */
export function getPlatformDisplayName(platform: string): string {
  const platformMap: Record<string, string> = {
    'square': 'Square',
    'square-sandbox': 'Square (Sandbox)',
    'quickbooks': 'QuickBooks',
    'quickbooks-sandbox': 'QuickBooks (Sandbox)',
    'shopify': 'Shopify',
    'shopify-sandbox': 'Shopify (Sandbox)',
  }

  return platformMap[platform] || platform
}

/**
 * Extract environment from platform value
 *
 * @param platform - Platform value from connection
 * @returns 'production' or 'sandbox'
 */
export function getPlatformEnvironment(platform: string): 'production' | 'sandbox' {
  return platform.includes('-sandbox') ? 'sandbox' : 'production'
}
